import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import * as actions from '~/store/actions';
import useTypedSelector from '~/hooks/useTypedSelector';
import * as NuacomComponent from '~/components';
import * as Loaders from '~/components/Loaders';
import * as lib from '~/dependencies';
import { useTryToCall, useNavigation, useConversation } from '~/hooks';
import { ChannelTypeEnum } from '~/enums/conversations';
import { Message } from '~/interfaces/conversations';
import { isValidInviteTarget, getChannelIconById } from '~/helpers';
import {
  ArrowLeftIcon,
  CallConversationIcon,
  SearchConversationIcon,
} from '~/assets/svgIcons';
import MessageList from './components/List';
import MessageTextInput from '../components/MessageTextInput';
import {
  Container,
  Block,
  Right,
  TitleContent,
  SearchBox,
  ClearSearchBtn,
  SearchInput,
  Loader,
} from './styles';

/**
 * Minimum characters to start searching in messages
 */
export const MIN_SEARCH_CARACTERS = 0;

type MessagesProps = any;

export default function Messages(props: MessagesProps) {
  const { location } = props;
  const {
    id,
    contactName: navParamsContactName,
    contactNumber: navParamsContactNumber,
  } = location.state || {};

  // HOOK's
  const call = useTryToCall();
  const navigate = useNavigation();
  const {
    addMessage,
    loading,
    setErrorMessage,
    errorMessage,
  } = useConversation();
  const dispatch = useDispatch();
  const { t } = lib.useTranslation();

  // STATE's
  const [searchBox, setSearchBox] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');

  // SELECTOR's
  const { chat: activeChat, isLoading } = useTypedSelector(
    state => state.conversation
  );

  // CONST's
  const {
    messages: allMessages,
    channelType,
    contactName: convContactName,
    contactNumber: convContactNumber,
  } = activeChat || {
    messages: [],
    channelType: ChannelTypeEnum.SMS,
  };

  const contactName = navParamsContactName || convContactName;
  const contactNumber = navParamsContactNumber || convContactNumber;
  const displayContactTitle = contactName || contactNumber;

  const messages =
    searchValue.length > MIN_SEARCH_CARACTERS
      ? allMessages.filter((e: Message) =>
          e.message.toLowerCase().includes(searchValue.toLowerCase())
        )
      : allMessages;

  const handleCallAction = () => {
    if (isValidInviteTarget(contactNumber)) {
      navigate.displayCall();
      call(contactNumber, null);
    }
  };

  const handleSendMessage = (message: string) => {
    if (!id) return;
    addMessage(id, message);
  };

  const handleCloseSearch = () => {
    setSearchBox(false);
    setSearchValue('');
  };

  useEffect(() => {
    dispatch(actions.setHiddenBottomNav(true));

    return () => {
      dispatch(actions.setHiddenBottomNav(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (id) dispatch(actions.fetchConversationById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (errorMessage) {
      lib.ToastMessage('error', errorMessage);
      setErrorMessage(null);
    }
  }, [errorMessage, setErrorMessage, t]);

  return (
    <Container>
      <NuacomComponent.Header {...props}>
        {!searchBox ? (
          <>
            <TitleContent>
              <lib.ReactSVG
                src={ArrowLeftIcon}
                className="icon-arrow-left"
                data-test-id="messages-back-button"
                onClick={() => navigate.conversations()}
              />
              <span data-test-id="messages-contact-name" title={contactNumber}>
                {displayContactTitle}
              </span>
              <lib.ReactSVG
                data-test-id="messages-channel-icon"
                src={getChannelIconById(channelType)}
              />
            </TitleContent>
            <Right>
              <IconButton
                size="small"
                className="icon-actions"
                title="Call"
                aria-label="Call"
                data-test-id="messages-call-button"
                onClick={handleCallAction}>
                <lib.ReactSVG src={CallConversationIcon} />
              </IconButton>
              <IconButton
                size="small"
                className="icon-actions"
                title="Search"
                aria-label="Search"
                data-test-id="messages-search-button"
                onClick={() => setSearchBox(true)}>
                <lib.ReactSVG src={SearchConversationIcon} />
              </IconButton>
            </Right>
          </>
        ) : (
          <SearchBox>
            <SearchInput
              id="search-in-messages"
              data-test-id="messages-search-input"
              name="search-in-messages"
              placeholder={t('screens.conversation.messages.searchInMessages')}
              autoFocus
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchValue(e.target.value)
              }
            />
            <ClearSearchBtn
              data-test-id="messages-clear-search"
              onClick={handleCloseSearch}>
              <Close fontSize="inherit" />
            </ClearSearchBtn>
          </SearchBox>
        )}
      </NuacomComponent.Header>

      {isLoading ? (
        <Loader>
          <Loaders.List numRows={11} />
        </Loader>
      ) : (
        <Block>
          <MessageList data={messages} searchValue={searchValue} />
          <MessageTextInput
            channelType={channelType}
            onSend={handleSendMessage}
            isLoading={loading}
          />
        </Block>
      )}
    </Container>
  );
}
